import type { Store } from '@reduxjs/toolkit';
import type { Exception } from '@sentry/core';
import { get } from 'es-toolkit/compat';

import { config } from '../../config';
import messagesEN from '../../features/translations/en-GB.json';
import { reportErrorToSentry } from '../setup/sentry';
import { trace } from '../setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

export const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

// biome-ignore lint/suspicious/noExplicitAny: dynamic imports can expose the value directly or as a default export
const normalizeDynamicImport = (imported: any) => get(imported, 'default', imported);

const displayMessageLoaders = import.meta.glob<Record<string, string>>(
    ['../../features/translations/*.json', '!../../features/translations/en-GB.json'],
    { import: 'default' }
);

export const importDisplayMessages = (locale: string) => {
    const localeLoader = displayMessageLoaders[`../../features/translations/${locale}.json`];
    const messagesPromise =
        locale === DEFAULT_LOCALE
            ? Promise.resolve(messagesEN)
            : (localeLoader?.() ?? Promise.reject(new Error(`Unsupported locale "${locale}"`)));

    return messagesPromise.then(normalizeDynamicImport).catch(error => {
        sendError(error);
        return error;
    });
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error as Exception);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
